import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Save, Info, ArrowRight } from 'lucide-react';

const CM3_TO_L = (v) => v ? (v / 1000).toFixed(1) : '0';

export default function AdminShipping() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('tapisstore_admin_token')) fetchConfig();
    else setLoading(false);
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/shipping-config');
      setPackages(res.data.packages || []);
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, raw) => {
    const value = field === 'price_eur'
      ? (parseFloat(raw) || 0)
      : field === 'max_volume_cm3'
        ? (raw === '' || raw === '0' ? null : parseInt(raw) || null)
        : raw;
    setPackages(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/shipping-config', { packages });
      toast.success('Configuration livraison sauvegardée !');
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Derive min from previous package's max
  const getMin = (index) => {
    if (index === 0) return 0;
    const prev = packages[index - 1];
    return prev?.max_volume_cm3 ? prev.max_volume_cm3 + 1 : null;
  };

  const regularPkgs = packages.filter(p => p.code !== 'sur_mesure');
  const surMesure = packages.find(p => p.code === 'sur_mesure');
  const surMesureIdx = packages.findIndex(p => p.code === 'sur_mesure');

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-shipping">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-semibold">Livraison</h1>
            <p className="text-muted-foreground mt-1">Définissez les plages de volume et les tarifs de chaque colis</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>

        {/* Explication */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8 flex gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 space-y-1">
            <p className="font-semibold">Règle de calcul automatique</p>
            <p>Le volume cumulé du panier (somme des volumes produits × quantités) détermine le colis. Le seuil "À" définit le volume <em>maximum</em> de chaque colis. Le seuil "De" est calculé automatiquement à partir du colis précédent.</p>
            <p>Le colis <strong>Sur mesure</strong> s'applique si aucun autre colis ne convient (volume trop grand).</p>
          </div>
        </div>

        {/* Tableau des colis standards */}
        <div className="rounded-xl border border-border overflow-hidden mb-6">
          {/* Header */}
          <div className="grid grid-cols-[2fr_3fr_2fr_2fr] gap-0 bg-muted/60 border-b border-border">
            <div className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Taille</div>
            <div className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plage de volume</div>
            <div className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Seuil max (cm³)</div>
            <div className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Prix livraison (€)</div>
          </div>

          {/* Rows */}
          {regularPkgs.map((pkg) => {
            const globalIdx = packages.findIndex(p => p.code === pkg.code);
            const minVol = getMin(globalIdx);
            const maxVol = pkg.max_volume_cm3;
            return (
              <div
                key={pkg.code}
                className="grid grid-cols-[2fr_3fr_2fr_2fr] gap-0 border-b border-border last:border-0 items-center hover:bg-muted/20 transition-colors"
              >
                {/* Label */}
                <div className="px-4 py-4">
                  <Input
                    value={pkg.label}
                    onChange={e => handleChange(globalIdx, 'label', e.target.value)}
                    className="font-semibold text-sm border-0 bg-transparent p-0 h-auto focus-visible:ring-0 focus-visible:border-b focus-visible:border-primary rounded-none"
                    data-testid={`shipping-label-${pkg.code}`}
                  />
                  <span className="text-xs font-mono text-muted-foreground mt-0.5 block">{pkg.code}</span>
                </div>

                {/* Volume range display */}
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 text-sm">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">De</p>
                      <p className="font-medium">
                        {minVol !== null ? `${minVol.toLocaleString()} cm³` : '—'}
                      </p>
                      {minVol !== null && (
                        <p className="text-xs text-muted-foreground">{CM3_TO_L(minVol)} L</p>
                      )}
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">À</p>
                      <p className="font-medium">
                        {maxVol ? `${maxVol.toLocaleString()} cm³` : <span className="text-muted-foreground italic text-xs">non défini</span>}
                      </p>
                      {maxVol && (
                        <p className="text-xs text-muted-foreground">{CM3_TO_L(maxVol)} L</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Max volume input */}
                <div className="px-4 py-4">
                  <Input
                    type="number"
                    min="1"
                    value={pkg.max_volume_cm3 ?? ''}
                    onChange={e => handleChange(globalIdx, 'max_volume_cm3', e.target.value)}
                    placeholder="Ex: 5000"
                    className="text-sm"
                    data-testid={`shipping-volume-${pkg.code}`}
                  />
                  {pkg.max_volume_cm3 && (
                    <p className="text-xs text-muted-foreground mt-1">= {CM3_TO_L(pkg.max_volume_cm3)} L</p>
                  )}
                </div>

                {/* Price input */}
                <div className="px-4 py-4">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={pkg.price_eur}
                      onChange={e => handleChange(globalIdx, 'price_eur', e.target.value)}
                      placeholder="Ex: 5.90"
                      className="text-sm"
                      data-testid={`shipping-price-${pkg.code}`}
                    />
                    <span className="text-muted-foreground text-sm shrink-0">€</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Sur mesure — separate card */}
        {surMesure && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Input
                    value={surMesure.label}
                    onChange={e => handleChange(surMesureIdx, 'label', e.target.value)}
                    className="font-semibold text-sm border-0 bg-transparent p-0 h-auto focus-visible:ring-0 w-auto"
                    data-testid="shipping-label-sur_mesure"
                  />
                  <span className="text-xs font-mono text-muted-foreground">{surMesure.code}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  S'applique automatiquement quand le volume dépasse le seuil du plus grand colis configuré ci-dessus.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div>
                  <Label className="text-xs text-muted-foreground">Prix (€) — 0 = devis</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={surMesure.price_eur}
                      onChange={e => handleChange(surMesureIdx, 'price_eur', e.target.value)}
                      placeholder="0 = devis"
                      className="w-32 text-sm"
                      data-testid="shipping-price-sur_mesure"
                    />
                    <span className="text-muted-foreground text-sm">€</span>
                  </div>
                  {surMesure.price_eur === 0 && (
                    <p className="text-xs text-amber-700 mt-1">Affichera "Devis sur demande" au client</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save reminder */}
        <div className="mt-6 flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer les modifications
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
